import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { NavItem } from '../../app/navigation';
import { LocaleLink } from './LocaleLink';


export function NavDropdown({ item, transparent }: { item: NavItem; transparent: boolean }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement | HTMLAnchorElement>(null);
  const panelId = useId();

  const children = item.children ?? [];


  const pressState = useRef<{ wasOpen: boolean; pointerType: string }>({
    wasOpen: false,
    pointerType: 'mouse',
  });


  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  const closeAndRefocus = () => {
    setOpen(false);
    triggerRef.current?.focus();
  };

  const focusFirstLink = () => {
    requestAnimationFrame(() => {
      document.getElementById(panelId)?.querySelector('a')?.focus();
    });
  };

  const triggerClasses = (isActive: boolean) =>
    `text-sm font-medium transition-colors ${
      transparent
        ? isActive || open
          ? 'text-white underline underline-offset-8'
          : 'text-white/80 hover:text-white'
        : isActive || open
          ? 'text-accent underline underline-offset-8'
          : 'text-content hover:text-accent'
    }`;

  return (
    <div
      ref={containerRef}
      className="relative"

      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setOpen(false);
      }}
      onKeyDown={(event) => {
        if (event.key === 'Escape' && open) {
          event.stopPropagation();
          closeAndRefocus();
        }
      }}
    >
      {item.href ? (
        <LocaleLink
          ref={triggerRef as React.Ref<HTMLAnchorElement>}
          to={item.href}
          end={item.href === '/'}
          aria-expanded={open}
          aria-controls={panelId}
          className={({ isActive }) => triggerClasses(isActive)}
          // Focus opens — this is what makes the panel reachable by keyboard at all.
          onFocus={() => setOpen(true)}
          onKeyDown={(event) => {
            if (event.key === 'ArrowDown') {
              event.preventDefault(); // don't scroll the page
              setOpen(true);
              focusFirstLink();
            }
          }}
        >
          {t(item.labelKey)}
        </LocaleLink>
      ) : (
        <button
          ref={triggerRef as React.Ref<HTMLButtonElement>}
          type="button"
          aria-haspopup="true"
          aria-expanded={open}
          aria-controls={panelId}
          className={triggerClasses(false)}
          // Focus opens — this is what makes the panel reachable by keyboard at all.
          onFocus={() => setOpen(true)}
          onPointerDown={(event) => {
            pressState.current = { wasOpen: open, pointerType: event.pointerType };
          }}
          /*
           * Tap/click must work: a touch device has no hover to open with.
           *
           * Only TOUCH gets toggle-to-close. With a mouse the panel is already open from hover
           * by the time the click lands, so toggling would shut it on the press — and since the
           * pointer never leaves, no new mouseenter would reopen it. It would read as broken.
           * Mouse users close it by moving away, pressing Esc, or clicking elsewhere.
           */
          onClick={() => {
            const closing = pressState.current.pointerType === 'touch' && pressState.current.wasOpen;
            setOpen(!closing);
          }}
          onKeyDown={(event) => {
            if (event.key === 'ArrowDown') {
              event.preventDefault();
              setOpen(true);
              focusFirstLink();
            }
          }}
        >
          {t(item.labelKey)}
        </button>
      )}

      {/*
        Kept in the DOM only while open. `hidden` would leave the links tabbable in some
        browsers, which is how a keyboard user ends up focused on something invisible.
      */}
      {open && children.length > 0 && (
        <div
          id={panelId}
          className="absolute left-0 top-full z-50 mt-3 min-w-52 rounded-2xl border border-line bg-panel py-2 shadow-soft dark:shadow-soft-dark"
        >
          <ul>
            {children.map((child) => (
              <li key={child.labelKey}>
                <LocaleLink
                  to={child.href}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `block whitespace-nowrap px-5 py-2 text-sm transition-colors ${
                      isActive
                        ? 'font-medium text-accent'
                        : 'text-content hover:bg-content/5 hover:text-accent'
                    }`
                  }
                >
                  {t(child.labelKey)}
                </LocaleLink>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
